import { useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import { shallow } from "zustand/shallow";
import * as yup from "yup";

import { InputText, InputTextArea } from "../../../../common/UI/Inputs";
import AppGetIcon from "../../../../common/UI/AppGetIcon";
import DataTable from "../../../../common/UI/DataTable/dataTable";
import Modal from "../../../../common/UI/Modal";
import AppInputSearch from "../../../../common/UI/Inputs/appInputSearch";
import iconPassView from "../../assets/login/icon-pass-view.svg";
import iconPass from "../../assets/login/icon-pass.svg";
import { useHeaderStore } from "../../../../store/header.store";

import style from "./Institutions.module.css";
import styleButton from "../../../../common/theme/styles/button.module.css";
import styleForm from "../../../../common/theme/styles/form.module.css";
import styleDataTable from "../../../../common/UI/DataTable/dataTable.module.css";
import { toast } from "react-toastify";
import { GetInstitutionsWithPaginationService } from "../../services/institutions/getInstitutionsWithPagination.service";
import { CreateOrUpdatedInstitutionsService, institutionData } from "../../services/institutions/createOrUpdateInstitutions.service";
import { DeleteInstitutionsService } from "../../services/institutions/deleteInstitutions.service";
import DeleteConfirmationModal from "../../../../common/UI/DataTable/DeleteConfirmationModal/deleteConfirmationModal";

const getInstitutionsWithPaginationService = new GetInstitutionsWithPaginationService();
const createOrUpdatedInstitutionsService = new CreateOrUpdatedInstitutionsService();
const deleteInstitutionsService = new DeleteInstitutionsService();

// Schema ajustado a todos los campos del formulario
// 1) Añade `id` al esquema para que yup lo conozca:
const UserSchema = yup.object().shape({
  id: yup
    .number()
    .nullable(), // o string().nullable() si tu id viene como string
  name: yup.string().required("Nombre es requerido"),
  description: yup.string().required("Descripción es requerido"),
});



export default function Institutions() {
  const headerStore = useHeaderStore(
    (state) => ({
      setModule: state.setModule,
      setModuleItem: state.setModuleItem,
    }),
    shallow
  );

  const [loading, setLoading] = useState(false);
  const [isReloadData, setIsReloadData] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isHoveredEdit, setIsHoveredEdit] = useState<number | null>(null);
  const [isOpenCurrentDelete, setIsOpenCurrentDelete] = useState<any>(null);
  const [isHoveredDelete, setIsHoveredDelete] = useState<number | null>(null);

  const [initialData, setInitialData] = useState<institutionData>({
    id: null,
    name: '',
    description: '',
  });

  const columns = [
    {
      header: "NOMBRE",
      field: "name",
    },
    {
      header: "DESCRIPCIÓN",
      field: "description",
    },
    {
      header: "OPCIONES",
      field: "",
      render: (row: any) => (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignContent: "center",
          gap: "5px",
        }}>

          <button
            className={styleDataTable.buttom_option}
            onClick={() => handleEdit(row)}
            onMouseEnter={() => setIsHoveredEdit(row.id)}
            onMouseLeave={() => setIsHoveredEdit(null)}
          >
            <AppGetIcon
              name="edit"
              classIcon={styleDataTable.sizeIcon}
              strokeColor="#1196ce"
              hoverColor={isHoveredEdit === row.id ? "#FFF" : undefined}
            />
            Editar
          </button>
          <button
            className={styleDataTable.buttom_option}
            onClick={() => {
              setIsOpenCurrentDelete(row);
            }}
            onMouseEnter={(e) => { setIsHoveredDelete(row?.id); }}
            onMouseLeave={(e) => { setIsHoveredDelete(null); }}
          >
            <AppGetIcon
              name="trash"
              classIcon={styleDataTable.sizeIcon}
              strokeColor="#1196ce"
              hoverColor={isHoveredDelete === row?.id ? "#FFF" : ""}
            />{" "}
            Eliminar
          </button>
        </div>

      ),
    },
  ];

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteInstitutionsService.run(isOpenCurrentDelete?.id,);
      toast.success(
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <AppGetIcon name="checkCircleFill" />
          Operación realizada correctamente
        </div>,
        { icon: false }
      );
      setIsReloadData(!isReloadData); // Recarga los datos después de la eliminación
      setIsOpenCurrentDelete(null); // Cierra el modal
      setLoading(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      toast.error(
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <AppGetIcon name="xCircleFill" />
          {errorMessage}
        </div>,
        { icon: false }
      );
      console.error("Error al eliminar:", error);
      setLoading(false);
    }
  };

  const handleCloseModalDelete = () => {
    // setInitialData(null);
    setIsOpenCurrentDelete(null);
  };


  // 2. En handleEdit, rellena también el id y organizationalJobId
  const handleEdit = (row: any) => {
    // ③ Finalmente seteas el estado inicial con esos valores:
    setInitialData({
      id: row.id,
      name: row.name,
      description: row.description,
    });

    setIsEditMode(true);
    setIsOpen(true);
  };


  const handleCloseModal = () => {
    setIsOpen(false);
    setInitialData({
      id: null,
      name: '',
      description: '',
    });
    setIsEditMode(false);
  }
  // 3. Llama al servicio en handleSubmit
  const handleSubmit = async (values: typeof initialData) => {
    setLoading(true);


    const payload: any = {
      id: isEditMode ? Number(values.id) : null,
      name: values.name,
      description: values.description,
    };

    try {
      const result = await createOrUpdatedInstitutionsService.run(payload, payload.id);
      toast.success(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="checkCircleFill" />
          {result.message}
        </div>,
        { icon: false }
      );
      setIsReloadData(prev => !prev);
      setIsOpen(false);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || "Error";
      toast.error(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="xCircleFill" />
          {errorMessage}
        </div>,
        { icon: false }
      );
    } finally {
      setLoading(false);
    }
  };

  const addButton = () => {
    setIsOpen(true);
  };
  useEffect(() => {
    headerStore.setModule("Administración", "");
    headerStore.setModuleItem("Administración de instituciones", "/admin/home");
  }, [headerStore]);

  return (
    <div className={style.containerQr}>
      <DataTable
        title="Lista de instituciones"
        columns={columns}
        service={getInstitutionsWithPaginationService}
        reloadData={isReloadData}
        addButton={addButton}
        excelButton={{
          enabled: true,
          nameFile: `Lista de Instituciones`,
          getExcelData: (data) => {
            return data.map((row) => ({
              NOMBRE: row.name,
              DESCRIPCIÓN: row.description,
            }));
          },
        }}
      >{''}</DataTable>

      <Modal
        title={isEditMode ? "Editar institución" : "Crear institución"}
        isOpen={isOpen}
        onClose={handleCloseModal}
        size={{ width: "80%", maxWidth: "600px", height: "" }}
        loadingModal={loading}
        footerContent={
          <div className={style.containerButtoms}>
            <button
              className={styleForm.buttomFooter + " " + styleForm.buttom_cancelar}
              onClick={handleCloseModal}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styleForm.buttomFooter}
              form="userForm"
              disabled={loading}
            >
              Guardar
            </button>
          </div>
        }
      >
        <Formik
          initialValues={initialData}
          validationSchema={UserSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values }) => (

            <Form id="userForm" className="flex-row">
              <div className={styleForm.form__container_row}>

                <InputText classNameInput={styleForm.input__input} type="text" name="name" placeholder="Nombres" TextLabel="Nombre*" />
              </div>

              <div className={styleForm.form__container_row}>
                <InputTextArea
                  name="description"
                  classNameInput={styleForm.input__inputWide}
                  placeholder="Descripción de la institución"
                  TextLabel="Descripción*"
                />
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
      {/* ✅ Modal de confirmación de eliminado (nuevo componente) */}
      <DeleteConfirmationModal
        isOpen={Boolean(isOpenCurrentDelete)}
        onClose={handleCloseModalDelete}
        onConfirm={handleDelete}
        loading={loading}
        itemName={isOpenCurrentDelete?.name}
        customMessage="¿Está seguro que desea eliminar esta institución?"
      />
    </div>
  );
}